/**
 * What the agent refuses of a call template, found before a save (Feature
 * 78 D18), so the canvas marks it and Save waits: a step linked from the
 * call that is not independent, and a reference to the call's result, which
 * does not exist when its steps start. The words and places are the agent's
 * own, so a problem found here reads as one a save would bring back. A step
 * after a decision that follows the call is after the call, and a decision's
 * branches read what its gate would (Feature 79 D18).
 */
import type { LinkageProblem } from '@optum/api-client/types';

import { FIRST, leavesCall, linkId, type Draft } from './draft.js';
import { isCall } from './start.js';

const FIRST_RESULT = `steps.${FIRST}.result`;

/** Whether a reference reads the call's result, or a field within it. */
function readsResult(ref: string): boolean {
  return ref === FIRST_RESULT || ref.startsWith(`${FIRST_RESULT}.`);
}

function unavailable(where: string, ref: string): LinkageProblem {
  return {
    where,
    code: 'first_result_unavailable',
    message: `${ref}: the call has no result when its steps start`,
  };
}

/** The problems a call template would be refused for; none on any other start. */
export function callProblems(draft: Draft): LinkageProblem[] {
  if (!isCall(draft.start)) return [];
  const problems: LinkageProblem[] = [];
  for (const link of draft.links) {
    const step = leavesCall(draft, link.from)
      ? draft.steps.find((s) => s.name === link.to)
      : undefined;
    if (step && step.mode !== 'independent') {
      problems.push({
        where: step.name,
        code: 'call_needs_independent',
        message:
          'a step after the call is independent: it runs beside the call and is delivered by webhook',
      });
    }
    for (const rule of link.rules.items) {
      if (readsResult(rule.ref)) problems.push(unavailable(linkId(link.from, link.to), rule.ref));
    }
  }
  for (const decision of draft.decisions) {
    const refs = decision.branches.flatMap((b) => (b.rules?.items ?? []).map((r) => r.ref));
    if (decision.ref) refs.push(decision.ref);
    for (const ref of refs.filter(readsResult)) problems.push(unavailable(decision.name, ref));
  }
  for (const step of draft.steps) {
    // A mapping's reference and a list filter's list both carry `ref`.
    for (const source of Object.values(step.mapping)) {
      if ('ref' in source && readsResult(source.ref)) {
        problems.push(unavailable(step.name, source.ref));
      }
    }
  }
  return problems;
}
